import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ContactFieldType } from '@/api/address-book/types';
import { _getBffContactById } from '@/api/address-book-bff/queries';
import BadgeIcon from '@/assets/svg/ui-elements/badge.svg';
import BuildingIcon from '@/assets/svg/ui-elements/building.svg';
import NoteIcon from '@/assets/svg/ui-elements/edit.svg';
import EditOutlineIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import GlobeIcon from '@/assets/svg/ui-elements/globe.svg';
import LinkIcon from '@/assets/svg/ui-elements/link-2.svg';
import LocationIcon from '@/assets/svg/ui-elements/location.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import PhoneIcon from '@/assets/svg/ui-elements/phone.svg';
import UserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { CATEGORY_VARIANT_COLORS, getCategoryVariant } from '@/pages/address-book/utils';
import { PagePaths } from '@/routes/page-paths';
import { Drawer, Spinner, theme, Typography } from '@/shared/design-system';
import { formatContactAddress } from '@/utils/format-contact-address';

import {
  AvatarPlaceholder,
  CategoryChip,
  CategoryChipDot,
  CategoryChipsList,
  ContactAvatar,
  ContactCard,
  ContactCardHeader,
  ContactFieldRow,
} from './styled';

interface SuccessStepProps {
  id?: string;
  isNew?: boolean;
  canOpenContact?: boolean;
  onFinish?: () => void;
}

type DirectFieldDef = {
  kind: 'direct';
  label: string;
  key: 'email' | 'name' | 'notes' | 'jobTitle';
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type ValuesFieldDef = {
  kind: 'values';
  label: string;
  fieldType: (typeof ContactFieldType)[keyof typeof ContactFieldType];
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type ContactFieldDef = DirectFieldDef | ValuesFieldDef;

const CONTACT_FIELDS: ContactFieldDef[] = [
  { kind: 'direct', label: 'Email', key: 'email', Icon: MailIcon },
  { kind: 'values', label: 'Email', fieldType: ContactFieldType.Email, Icon: MailIcon },
  { kind: 'direct', label: 'Full Name', key: 'name', Icon: UserIcon },
  { kind: 'values', label: 'Phone', fieldType: ContactFieldType.Phone, Icon: PhoneIcon },
  { kind: 'direct', label: 'Job title', key: 'jobTitle', Icon: BuildingIcon },
  { kind: 'values', label: 'Address', fieldType: ContactFieldType.Address, Icon: LocationIcon },
  { kind: 'values', label: 'Website', fieldType: ContactFieldType.Website, Icon: LinkIcon },
  { kind: 'direct', label: 'Notes', key: 'notes', Icon: NoteIcon },
  { kind: 'values', label: 'Social Media', fieldType: ContactFieldType.Social, Icon: GlobeIcon },
];

export const SuccessStep = ({ id, isNew, canOpenContact = true, onFinish }: SuccessStepProps) => {
  const navigate = useNavigate();

  const { data: contact } = useQuery(_getBffContactById(id));

  const displayName = contact?.name;

  const categories = useMemo(
    () =>
      (contact?.categories || []).map((cat) => ({
        id: cat.nodeId,
        name: cat.nodeName,
        color: CATEGORY_VARIANT_COLORS[getCategoryVariant(cat.rootName)],
      })),
    [contact?.categories],
  );

  const fields = useMemo(() => {
    if (!contact) return [];
    return CONTACT_FIELDS.flatMap((def) => {
      if (def.kind === 'direct') {
        const value = contact[def.key];
        return value ? [{ label: def.label, value, Icon: def.Icon }] : [];
      }
      return (contact.values ?? [])
        .filter((v) => v.fieldType === def.fieldType)
        .map((v) => ({
          label: v.label ?? def.label,
          value: v.fieldType === ContactFieldType.Address ? formatContactAddress(v.value) : v.value,
          Icon: def.Icon,
        }));
    });
  }, [contact]);

  const onEditCategories = () => {
    if (!contact) return;
    navigate(
      contact.referenceId
        ? generatePath(PagePaths.userProfile, { id: contact.referenceId })
        : generatePath(PagePaths.addressBookContact, { id: contact.id }),
    );
  };

  const description = displayName
    ? isNew
      ? `You're now connected with ${displayName}`
      : `You're already connected with ${displayName}`
    : undefined;

  return (
    <Drawer
      open
      onOpenChange={(isOpen) => !isOpen && onFinish?.()}
      title="Contact Added!"
      description={description}
      maxWidth={600}
      leftAction={
        contact && canOpenContact
          ? {
              children: (
                <>
                  <EditOutlineIcon width={16} height={16} />
                  Edit Categories
                </>
              ),
              onClick: onEditCategories,
            }
          : undefined
      }
      primaryAction={{ children: 'Confirm', onClick: () => onFinish?.() }}
    >
      {!contact ? (
        <Stack justifyContent="center" alignItems="center" padding="40px">
          <Spinner />
        </Stack>
      ) : (
        <Stack flexDirection="column" gap="16px">
          <ContactCard>
            <ContactCardHeader>
              {contact.iconUrl ? (
                <ContactAvatar src={contact.iconUrl} alt={displayName ?? 'Contact'} />
              ) : (
                <AvatarPlaceholder>
                  <Typography variant="h6">{displayName?.[0]?.toUpperCase() ?? ''}</Typography>
                </AvatarPlaceholder>
              )}
              {displayName && (
                <Typography variant="h6" fontWeight={600}>
                  {displayName}
                </Typography>
              )}
            </ContactCardHeader>

            <Stack flexDirection="column">
              {fields.map(({ label, value, Icon }, idx) => (
                <ContactFieldRow key={`${value}_${idx}`}>
                  <Stack alignItems="center" gap="10px">
                    <Icon width={16} height={16} />
                    <Typography variant="body3" color={theme.colors.white[60]}>
                      {label}
                    </Typography>
                  </Stack>
                  <Typography variant="body3">{value}</Typography>
                </ContactFieldRow>
              ))}
            </Stack>
          </ContactCard>

          {categories.length > 0 && (
            <Stack flexDirection="column" gap="10px">
              <Stack alignItems="center" gap="6px">
                <BadgeIcon width={14} height={14} />
                <Typography variant="body3" fontWeight={500}>
                  Categories ({categories.length})
                </Typography>
              </Stack>
              <CategoryChipsList>
                {categories.map(({ id: categoryId, name, color }) => (
                  <CategoryChip key={categoryId} $color={color}>
                    <CategoryChipDot $color={color} />
                    <Typography variant="body4" fontWeight={500}>
                      {name}
                    </Typography>
                  </CategoryChip>
                ))}
              </CategoryChipsList>
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  );
};
